import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { RECIPE_LIST as RL } from '../constants/strings';
import { formatTime } from '../utils/time';
import { getKiLabel, getKiTooltip } from '../utils/recipe';
import { useShoppingList } from '../context/ShoppingListContext';
import { useAuth } from '../context/AuthContext';
import { IconNavShopping, IconCheck, IconClose, IconWand } from '../components/icons';
import './RecipeList.mobile.css';

const PAGE_SIZE = 20;

const CATEGORY_ICONS = {
  'Hauptgerichte':        '🍽️',
  'Suppen & Eintöpfe':    '🥘',
  'Salate':               '🥗',
  'Pasta & Nudeln':       '🍝',
  'Reis & Getreide':      '🍚',
  'Fleisch & Geflügel':   '🥩',
  'Geflügel':             '🍗',
  'Fisch & Meeresfrüchte':'🐟',
  'Vegetarisch':          '🥦',
  'Vegan':                '🌱',
  'Desserts & Süßes':     '🍰',
  'Frühstück & Brunch':   '🥐',
  'Snacks & Fingerfood':  '🍿',
  'Pizza & Flammkuchen':  '🍕',
  'Aufläufe & Gratins':   '🫕',
  'Grill & BBQ':          '🔥',
  'Aus dem Ofen':         '♨️',
  'Hülsenfrüchte':        '🫘',
  'Gemüse & Beilagen':    '🥕',
  'Festliche Gerichte':   '🎉',
};

export default function RecipeListMobile() {
  const { user } = useAuth();
  const { addItems } = useShoppingList();
  const [addedId, setAddedId] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.getCategories()
      .then(d => setCategories(d.categories))
      .catch(console.error);
  }, []);

  useEffect(() => { loadInitial(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInitial() {
    setLoading(true);
    setLoadError(false);
    setOffset(0);
    setRecipes([]);
    try {
      let data;
      if (user) {
        data = await api.getRecipes({
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: 0,
          public_only: true,
        });
      } else {
        data = await api.getPublicRecipes({
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: 0,
        });
      }
      setRecipes(data.recipes);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    const newOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    try {
      let data;
      if (user) {
        data = await api.getRecipes({
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: newOffset,
          public_only: true,
        });
      } else {
        data = await api.getPublicRecipes({
          category: category || undefined,
          limit: PAGE_SIZE,
          offset: newOffset,
        });
      }
      setRecipes(prev => [...prev, ...data.recipes]);
      setOffset(newOffset);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleAddToCart(recipe) {
    if (addedId === recipe.id) return;
    setAddedId(recipe.id);
    try {
      const data = await api.getRecipe(recipe.id);
      const ings = data.recipe?.ingredients || [];
      addItems(ings.map(ing => ({
        name: ing.name,
        amount: [ing.amount, ing.unit].filter(Boolean).join(' '),
        recipeId: recipe.id,
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => setAddedId(null), 1500);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!search.trim()) { loadInitial(); return; }
    setLoading(true);
    try {
      let results;
      if (user) {
        const data = await api.searchRecipes(search);
        results = data.recipes.filter(r => r.is_public);
      } else {
        const data = await api.getPublicRecipes({ search: search.trim(), limit: PAGE_SIZE, offset: 0 });
        results = data.recipes;
      }
      setRecipes(results);
      setTotal(results.length);
      setOffset(9999);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearch('');
    loadInitial();
  }

  const hasMore = recipes.length < total && offset < 9000;

  return (
    <div className="rl-mobile">
      {/* Header */}
      <div className="rl-mobile-header">
        <div>
          <h1>{RL.title}</h1>
          <p className="rl-mobile-subtitle">{RL.subtitle(total)}</p>
        </div>
        <Link to="/upload" className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '0.45rem 0.85rem' }}>
          + Scannen
        </Link>
      </div>

      {/* Suche */}
      <form onSubmit={handleSearch} className="rl-mobile-search-bar">
        <input
          type="text"
          placeholder="Suchen …"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="rl-mobile-search-input"
        />
        {search && (
          <button type="button" className="rl-mobile-search-clear" onClick={clearSearch}><IconClose size={14} /></button>
        )}
        <button type="submit" className="rl-mobile-search-btn">Suchen</button>
      </form>

      {/* Kategorie-Chips */}
      <div className="rl-mobile-chips">
        <button
          className={`rl-mobile-chip ${category === '' ? 'active' : ''}`}
          onClick={() => setCategory('')}
        >
          🍴 Alle
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            className={`rl-mobile-chip ${category === cat ? 'active' : ''}`}
            onClick={() => setCategory(cat)}
          >
            {CATEGORY_ICONS[cat] || '🍴'} {cat}
          </button>
        ))}
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="rl-mobile-loading">
          <div className="loader" />
          <p>{RL.loading}</p>
        </div>
      ) : loadError ? (
        <div className="rl-mobile-empty">
          <h3>{RL.errorTitle}</h3>
          <p>{RL.errorText}</p>
        </div>
      ) : recipes.length === 0 ? (
        <div className="rl-mobile-empty">
          <h3>{RL.emptyTitle}</h3>
          <p>{RL.emptyText}</p>
        </div>
      ) : (
        <>
          <div className="rl-mobile-grid">
            {recipes.map(recipe => (
              <div key={recipe.id} className="rl-mobile-card-wrap">
                <Link to={`/recipe/${recipe.id}`} className="rl-mobile-card">
                  <div className="rl-mobile-card-img-wrap">
                    {recipe.image_path ? (
                      <img src={recipe.image_path} alt={recipe.title} className="rl-mobile-card-img" />
                    ) : (
                      <div className="rl-mobile-card-placeholder">
                        {CATEGORY_ICONS[recipe.category] || '🍽️'}
                      </div>
                    )}
                  </div>
                  <div className="rl-mobile-card-body">
                    <div className="rl-mobile-card-title">{recipe.title}</div>
                    {recipe.category && (
                      <span className="rl-mobile-card-category">
                        {CATEGORY_ICONS[recipe.category] || ''} {recipe.category}
                      </span>
                    )}
                    {getKiLabel(recipe.content_source) && (
                      <span className="ki-badge-inline" title={getKiTooltip(recipe.content_source)}>
                        <IconWand size={12} /> {getKiLabel(recipe.content_source)}
                      </span>
                    )}
                    <div className="rl-mobile-card-meta">
                      {recipe.prep_time > 0 && <span>⏱ {formatTime(recipe.prep_time)}</span>}
                      {recipe.servings && <span>🍽 {recipe.servings} Port.</span>}
                    </div>
                  </div>
                </Link>
                <button
                  className={`rl-mobile-cart-btn${addedId === recipe.id ? ' added' : ''}`}
                  onClick={() => handleAddToCart(recipe)}
                  title="Zutaten zur Einkaufsliste hinzufügen"
                  aria-label="Zutaten zur Einkaufsliste hinzufügen"
                >
                  {addedId === recipe.id ? <IconCheck size={16} /> : <IconNavShopping size={16} />}
                </button>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="rl-mobile-load-more">
              <button
                className="rl-mobile-load-more-btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? RL.loadMoreLoading : RL.loadMore(total - recipes.length)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
